/**
 * Implements an immutable cache from URLs to major conversions.
 * Used to track Learning Time when users perform client-side navigation,
 * especially involving modals.
 *
 * The cache is represented as a stack of slugs. For example, suppose we have a
 * conversion C at /foo and a conversion D at /foo/bar?baz=1&qux=2.
 *
 *     /foo/bar?baz=1&qux=2
 *      ^   ^
 *      C   D
 *
 * These conversions will be cached as follows:
 *
 *     [{slug: ""},
 *      {slug: "foo", conversion: C},
 *      {slug: "bar?baz=1&qux=2", conversion: D}]
 *
 * Now suppose we perform a navigation back to /foo. We can pop back to:
 *
 *     [{slug: ""},
 *      {slug: "foo", conversion: C}]
 *
 * and access conversion C. Next, navigating to /foo/bar/qux with conversion E:
 *
 *     [{slug: ""},
 *      {slug: "foo", conversion: C},
 *      {slug: "bar"},
 *      {slug: "qux", conversion: E}]
 *
 * appends to the stack. Finally, visiting /foo/corge/grault with conversion F
 * replaces the tail of the stack with the new path, while retaining C (since
 * /foo is part of both paths):
 *
 *     [{slug: ""},
 *      {slug: "foo", conversion: C},
 *      {slug: "corge"},
 *      {slug: "grault", conversion: F}]
 *
 * Navigating away to /plugh with conversion G replaces the whole cache.
 *
 *     [{slug: ""},
 *      {slug: "plugh", conversion: G}]
 *
 * This construction allows us to retain conversions from previous navigations
 * when we are likely to return to them, while bounding memory usage to the
 * maximum number of slugs in a URL, and avoiding stale entries.
 */

import {parseUrl} from "@khan/urls";

import type {MajorConversion} from "./types.ts";

export type URLCache = Array<URLCacheNode>;

interface URLCacheNode {
    slug: string;
    conversion?: MajorConversion;
}

export const INITIAL_URL_CACHE: URLCache = [];

/**
 * Splits a URL into slug segments, appending all query parameters to the end.
 */
export const tokenize = (href: string): Array<string> => {
    const pathWithQuery = parseUrl(href);
    const {pathname, search} = pathWithQuery;

    // If the path ends in a trailing slash, delete it.
    const path = pathname.endsWith("/")
        ? pathname.substring(0, pathname.length - 1)
        : pathname;

    const suffix = `${path}${search}`;
    const tokens = suffix.split("/");
    return tokens;
};

/**
 * Initializes a URLCache from a major conversion.
 */
export const initialCache = (conversion: MajorConversion): URLCache => {
    const {urlForDebugging} = conversion.learningTimeInfo;
    const slugs = tokenize(urlForDebugging);
    const lastIndex = slugs.length - 1;
    return slugs.map((slug, i) => {
        return {
            slug,
            ...(i === lastIndex ? {conversion} : null),
        };
    });
};

/**
 * Given a URLCache and a new major conversion, returns a new URLCache that
 * accounts for the new major conversion, preserving any relevant conversions
 * from the old cache.
 */
export function updateUrlCache(
    cached: URLCache,
    conversion: MajorConversion,
): URLCache {
    const target = initialCache(conversion);

    // The new cache length should never be longer than the target so map over
    // the target to obtain the new cache. Keep anything that has been cached
    // (with the same slug) up until just before last node in the target.
    const newCache = target.map((targetNode, i) => {
        // Always keep the last targetNode as it has the Conversion
        // that we are actually trying to update the cache with.
        if (i === target.length - 1) {
            return targetNode;
        }

        // Anything else in the cache with a matching slug should be kept
        if (i < cached.length && cached[i].slug === targetNode.slug) {
            return cached[i];
        }

        return targetNode;
    });

    return newCache;
}

/**
 * Given a URLCache and a URL for a minor conversion, returns the cached major
 * conversion for that URL, if one exists.
 */
export const getConversionForUrl = (
    cached: URLCache,
    url: string,
): MajorConversion | null => {
    // If the URL is longer than the cache, it's definitely a miss.
    // However, the URL may be shorter than the cache and still hit.
    const slugs = tokenize(url);
    if (slugs.length > cached.length) {
        return null;
    }

    // Iterate over the slugs and check if they match what's cached.
    let lastNode: URLCacheNode | null | undefined = null;
    for (let i = 0; i < slugs.length; i += 1) {
        const slug = slugs[i];
        const node = cached[i]; // Guaranteed to exist since |slugs| <= |cached|

        // If the slugs ever diverge from the cache, it's definitely a miss.
        if (slug !== node.slug) {
            return null;
        }
        lastNode = node;
    }

    const hit = lastNode && lastNode.conversion;
    return hit || null;
};
